import { useEffect, useRef, useState, type FormEvent } from 'react';
import { createEvent, updateEvent } from '../api.js';
import { ScribeChat } from '../components/Scribe.js';
import {
  captureText,
  createLifeNote,
  createTask,
  listProjects,
  undoCapture,
  updateLifeNote,
  updateTask,
  type CaptureResult,
} from '../lifeos/api.js';
import Sheet from '../lifeos/Sheet.js';
import { useSpheres } from '../lifeos/spheres.js';
import { errorText, useToast } from '../lifeos/toast.js';
import type { Project, Sphere } from '../lifeos/types.js';
import Icon from '../ui/Icon.js';
import { speechCtor, speechErrorText, transcriptOf, type SpeechRecognitionLike } from './speech.js';

export type CaptureMode = 'voice' | 'type' | 'chat';
type Kind = CaptureResult['kind'];
type Made = Pick<CaptureResult, 'kind' | 'task' | 'event' | 'note'>;

/** Where a capture landed, as the receipt card edits it. */
interface Filed {
  kind: Kind;
  /** The row Undo removes (task / event / note id inside). */
  made: Made;
  title: string;
  sphereKey: string;
  projectId: string | null;
  /** Task: due day 'YYYY-MM-DD'. Event: start instant (ISO). Note: unused. */
  when: string | null;
  endsAt: string | null;
  allDay: boolean;
}

interface Job {
  id: number;
  text: string;
  status: 'filing' | 'done';
  summary: string;
  filed: Filed | null;
  /** A refused Undo / kind change explains itself on its card. */
  note: string | null;
}

const KINDS: { value: Kind; label: string }[] = [
  { value: 'task', label: 'Task' },
  { value: 'event', label: 'Event' },
  { value: 'note', label: 'Note' },
];

function metaOf(meta: unknown): Record<string, unknown> {
  return meta !== null && typeof meta === 'object' && !Array.isArray(meta) ? (meta as Record<string, unknown>) : {};
}

/** The receipt's editable fields, read off what `/api/capture` made. */
function filedFrom(r: CaptureResult): Filed {
  const made: Made = { kind: r.kind, task: r.task, event: r.event, note: r.note };
  if (r.kind === 'event' && r.event) {
    const m = metaOf(r.event.meta);
    const cap = metaOf(m.capture);
    const sphere = (typeof m.sphereKey === 'string' && m.sphereKey) || (typeof cap.sphereKey === 'string' && cap.sphereKey) || r.event.domain || 'personal';
    const project = (typeof m.projectId === 'string' && m.projectId) || (typeof cap.projectId === 'string' && cap.projectId) || null;
    return {
      kind: 'event',
      made,
      title: r.event.title,
      sphereKey: sphere,
      projectId: project,
      when: r.event.startsAt,
      endsAt: r.event.endsAt ?? null,
      allDay: r.event.allDay ?? false,
    };
  }
  if (r.kind === 'note' && r.note) {
    return {
      kind: 'note',
      made,
      title: r.note.title ?? r.note.body,
      sphereKey: r.note.sphereKey,
      projectId: r.note.projectId,
      when: null,
      endsAt: null,
      allDay: false,
    };
  }
  return {
    kind: 'task',
    made,
    title: r.task?.title ?? '',
    sphereKey: r.task?.sphereKey ?? 'personal',
    projectId: r.task?.projectId ?? null,
    when: r.task?.dueOn ?? null,
    endsAt: null,
    allDay: false,
  };
}

const pad = (n: number): string => String(n).padStart(2, '0');
/** ISO instant → 'YYYY-MM-DDTHH:MM' for a datetime-local input (local zone). */
function localInput(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function localDay(iso: string): string {
  return localInput(iso).slice(0, 10);
}
/** Local 'YYYY-MM-DD' + 'HH:MM' → ISO. */
function isoAt(day: string, time: string): string {
  const [y, m, d] = day.split('-').map(Number);
  const [h, mi] = time.split(':').map(Number);
  return new Date(y!, m! - 1, d!, h ?? 0, mi ?? 0).toISOString();
}
function todayLocal(): string {
  return localDay(new Date().toISOString());
}

/**
 * Capture: speak (or type) one thing, it is filed by `POST /api/capture` as a
 * task, event or note (model-routed server-side, which takes a few seconds).
 * The sheet answers at once with a "Got it — filing…" card, then turns it
 * into an editable receipt: kind, title, job, project and date, each saved
 * through the row's own endpoint. Undo drops the task (repeat:null cancels a
 * series) or deletes the event/note and puts the words back to fix. More
 * captures can be filed while one is still in flight. Closing with a receipt
 * hands its Undo to the toast; a capture still filing when the sheet closes
 * reports in a toast when it lands. "Chat" opens the Scribe.
 */
export default function CaptureSheet({
  initialMode,
  onClose,
  onChanged,
}: {
  initialMode: CaptureMode;
  onClose: () => void;
  onChanged: () => void;
}): JSX.Element {
  const toast = useToast();
  const spheres = useSpheres();
  const canSpeak = speechCtor() !== null;
  const [mode, setMode] = useState<CaptureMode>(initialMode === 'voice' && !canSpeak ? 'type' : initialMode);
  const [text, setText] = useState('');
  const [interim, setInterim] = useState('');
  const [listening, setListening] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  /** Receipts on screen (vs. the capture field, e.g. after "Add another"). */
  const [showReceipts, setShowReceipts] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const rec = useRef<SpeechRecognitionLike | null>(null);
  const heard = useRef('');
  const cancelled = useRef(false);
  const mounted = useRef(true);
  const field = useRef<HTMLTextAreaElement>(null);
  const jobsRef = useRef<Job[]>([]);
  jobsRef.current = jobs;
  const nextId = useRef(1);

  // Stop the mic on unmount; the newest un-undone receipt keeps its Undo in a toast.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      cancelled.current = true;
      rec.current?.abort();
      const last = [...jobsRef.current].reverse().find((j) => j.status === 'done' && j.filed);
      if (last?.filed) {
        const made = last.filed.made;
        toast.undoable({ text: last.summary, undo: () => undoCapture(made), onUndone: onChanged });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    listProjects().then(
      (ps) => mounted.current && setProjects(ps),
      () => undefined,
    );
  }, []);

  // Voice mode starts listening straight away: the tap that opened the sheet is the gesture.
  useEffect(() => {
    if (initialMode === 'voice' && canSpeak) startListening();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const receipts = showReceipts && jobs.length > 0;
  useEffect(() => {
    if (mode === 'type' && !receipts) field.current?.focus();
  }, [mode, receipts]);

  function patchJob(id: number, fn: (j: Job) => Job): void {
    setJobs((js) => js.map((j) => (j.id === id ? fn(j) : j)));
  }

  function startListening(): void {
    const Ctor = speechCtor();
    if (!Ctor) return;
    setNote(null);
    heard.current = '';
    setInterim('');
    setText('');
    cancelled.current = false;
    let r: SpeechRecognitionLike;
    try {
      r = new Ctor();
    } catch {
      setMode('type');
      setNote(speechErrorText('unknown'));
      return;
    }
    r.lang = navigator.language || 'en-US';
    r.continuous = false;
    r.interimResults = true;
    // Every handler ignores a recognizer that is no longer the current one
    // (aborted by a mode switch, or by React StrictMode's remount in dev).
    r.onresult = (e) => {
      if (rec.current !== r) return;
      const t = transcriptOf(e);
      if (t.final) heard.current = t.final;
      setInterim([t.final, t.interim].filter(Boolean).join(' '));
    };
    r.onerror = (e) => {
      if (rec.current !== r || e.error === 'aborted') return;
      setNote(speechErrorText(e.error));
      if (e.error !== 'no-speech') setMode('type');
    };
    r.onend = () => {
      if (rec.current !== r) return;
      setListening(false);
      rec.current = null;
      const said = (heard.current || '').trim();
      if (!cancelled.current && said) submit(said);
    };
    rec.current = r;
    try {
      r.start();
      setListening(true);
    } catch {
      rec.current = null;
      setMode('type');
      setNote(speechErrorText('unknown'));
    }
  }

  function stopListening(): void {
    rec.current?.stop();
  }

  function switchTo(m: CaptureMode): void {
    if (rec.current) {
      cancelled.current = true;
      rec.current.abort();
      rec.current = null;
      setListening(false);
    }
    if (m === 'type' && interim && !text) setText(interim);
    setNote(null);
    setMode(m);
  }

  /** File it: the card shows "filing…" at once; the receipt replaces it when the server answers. */
  function submit(value: string): void {
    const v = value.trim();
    if (!v) return;
    const id = nextId.current++;
    setJobs((js) => [...js, { id, text: v, status: 'filing', summary: '', filed: null, note: null }]);
    setShowReceipts(true);
    setNote(null);
    setText('');
    setInterim('');
    captureText(v).then(
      (r) => {
        onChanged();
        if (!mounted.current) {
          // Closed while filing: report where it went, Undo included.
          toast.undoable({ text: r.summary, undo: () => undoCapture(r), onUndone: onChanged });
          return;
        }
        patchJob(id, (j) => ({ ...j, status: 'done', summary: r.summary, filed: filedFrom(r) }));
      },
      (err: unknown) => {
        const why = `Couldn't add that: ${errorText(err)}`;
        if (!mounted.current) {
          toast.error(why);
          return;
        }
        setJobs((js) => js.filter((j) => j.id !== id));
        // Give the words back to fix, unless something else is already in the field.
        setText((t) => (t.trim() ? t : v));
        setMode('type');
        setNote(why);
        if (jobsRef.current.filter((j) => j.id !== id).length === 0) setShowReceipts(false);
        else toast.error(why);
      },
    );
  }

  async function undo(job: Job): Promise<void> {
    const f = job.filed;
    if (!f) return;
    try {
      await undoCapture(f.made);
    } catch (err) {
      patchJob(job.id, (j) => ({ ...j, note: `Couldn't undo: ${errorText(err)}` }));
      return;
    }
    onChanged();
    const rest = jobsRef.current.filter((j) => j.id !== job.id);
    setJobs(rest);
    if (rest.length === 0) {
      setShowReceipts(false);
      setText(job.text);
      setMode('type');
      setNote('Undone — fix it and add again.');
    } else {
      toast.info('Undone');
    }
  }

  /** Optimistic edit: show it now, save it, put it back with a toast if the save fails. */
  async function edit(job: Job, change: Partial<Filed>, save: (f: Filed) => Promise<unknown>): Promise<void> {
    const before = job.filed;
    if (!before) return;
    const after = { ...before, ...change };
    patchJob(job.id, (j) => ({ ...j, filed: after, note: null }));
    try {
      await save(after);
      onChanged();
    } catch (err) {
      patchJob(job.id, (j) => ({ ...j, filed: before }));
      toast.error(`Couldn't save: ${errorText(err)}`);
    }
  }

  function saveFields(f: Filed, fields: { title?: boolean; filing?: boolean; when?: boolean }): Promise<unknown> {
    const sphereKey = f.sphereKey;
    if (f.kind === 'task' && f.made.task) {
      return updateTask(f.made.task.id, {
        ...(fields.title ? { title: f.title } : {}),
        ...(fields.filing ? { sphereKey, projectId: f.projectId } : {}),
        ...(fields.when ? { dueOn: f.when } : {}),
      });
    }
    if (f.kind === 'event' && f.made.event) {
      return updateEvent(f.made.event.id, {
        ...(fields.title ? { title: f.title } : {}),
        ...(fields.filing ? { sphereKey, projectId: f.projectId } : {}),
        ...(fields.when && f.when ? { startsAt: f.when, endsAt: f.endsAt, allDay: f.allDay } : {}),
      });
    }
    if (f.kind === 'note' && f.made.note) {
      return updateLifeNote(f.made.note.id, {
        ...(fields.title ? { title: f.title } : {}),
        ...(fields.filing ? { sphereKey, projectId: f.projectId } : {}),
      });
    }
    return Promise.resolve();
  }

  /** Change what it is: make the new row first, then remove the old one (a refused removal rolls the new one back). */
  async function changeKind(job: Job, kind: Kind): Promise<void> {
    const f = job.filed;
    if (!f || f.kind === kind) return;
    const sphereKey = f.sphereKey;
    const day = f.kind === 'event' && f.when ? localDay(f.when) : f.when;
    let made: Made;
    let next: Filed;
    try {
      if (kind === 'task') {
        const task = await createTask({ title: f.title, sphereKey, projectId: f.projectId, dueOn: day, status: 'next' });
        made = { kind, task };
        next = { ...f, kind, made, when: task.dueOn, endsAt: null, allDay: false };
      } else if (kind === 'event') {
        const on = day ?? todayLocal();
        const event = await createEvent({
          title: f.title,
          startsAt: isoAt(on, '12:00'),
          allDay: true,
          kind: 'event',
          domain: sphereKey,
          meta: { sphereKey, projectId: f.projectId, capture: { sphereKey, projectId: f.projectId, via: 'web' } },
        });
        made = { kind, event };
        next = { ...f, kind, made, when: event.startsAt, endsAt: null, allDay: true };
      } else {
        const created = await createLifeNote({ body: job.text, title: f.title, sphereKey, projectId: f.projectId });
        made = { kind, note: created };
        next = { ...f, kind, made, when: null, endsAt: null, allDay: false };
      }
    } catch (err) {
      patchJob(job.id, (j) => ({ ...j, note: `Couldn't change it: ${errorText(err)}` }));
      return;
    }
    try {
      await undoCapture(f.made);
    } catch (err) {
      await undoCapture(made).catch(() => undefined);
      patchJob(job.id, (j) => ({ ...j, note: `Couldn't change it: ${errorText(err)}` }));
      return;
    }
    const label = KINDS.find((k) => k.value === kind)!.label.toLowerCase();
    patchJob(job.id, (j) => ({ ...j, filed: next, summary: `Now a ${label}: ${f.title}`, note: null }));
    onChanged();
  }

  function another(): void {
    setShowReceipts(false);
    setNote(null);
    if (mode === 'voice' && canSpeak) startListening();
    else setMode('type');
  }

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    submit(text);
  }

  const filing = jobs.filter((j) => j.status === 'filing').length;
  const filed = jobs.length - filing;

  return (
    <Sheet title={mode === 'chat' ? 'Chat with the scribe' : 'Capture'} onClose={onClose} className="cap">
      {receipts ? (
        <div className="cap__receipt" role="status">
          {jobs.map((job) =>
            job.status === 'filing' || !job.filed ? (
              <div key={job.id} className="cap__filing" aria-busy="true">
                <div className="cap__filing-head">
                  <span className="cap__filing-spin" aria-hidden="true" />
                  <span className="cap__filing-label">Got it — filing…</span>
                </div>
                <p className="cap__filing-text">“{job.text}”</p>
                <span className="cap__shimmer" aria-hidden="true" />
              </div>
            ) : (
              <ReceiptCard
                key={job.id}
                job={job}
                filed={job.filed}
                spheres={spheres}
                projects={projects}
                onUndo={() => void undo(job)}
                onKind={(k) => void changeKind(job, k)}
                onEdit={(change, fields) => void edit(job, change, (f) => saveFields(f, fields))}
              />
            ),
          )}
          {/* Single-capture errors (e.g. 409: already on the calendar — delete it there) explain themselves here. */}
          {note && <p className="cap__note">{note}</p>}
          <div className="cap__receipt-actions cap__receipt-actions--one">
            <button type="button" className="ui-btn ui-btn--secondary ui-btn--md" onClick={another}>
              <Icon name="plus" size={18} />
              Add another
            </button>
          </div>
          <button type="button" className="ui-btn ui-btn--primary ui-btn--lg ui-btn--block" onClick={onClose}>
            Done
          </button>
        </div>
      ) : mode === 'chat' ? (
        <div className="cap__chat">
          <ScribeChat className="scribe scribe--embedded" />
          <button type="button" className="cap__switch" onClick={() => switchTo(canSpeak ? 'voice' : 'type')}>
            <Icon name="chevron-left" size={18} />
            Back to capture
          </button>
        </div>
      ) : (
        <>
          {jobs.length > 0 && (
            <button type="button" className="cap__queue" onClick={() => setShowReceipts(true)}>
              {filing > 0 && <span className="cap__filing-spin" aria-hidden="true" />}
              {[filing ? `${filing} filing…` : '', filed ? `${filed} filed` : ''].filter(Boolean).join(' · ')}
              <span className="cap__queue-go">Review</span>
            </button>
          )}
          {mode === 'voice' ? (
            <div className="cap__voice">
              <p className={`cap__transcript${interim ? '' : ' cap__transcript--empty'}`} aria-live="polite">
                {interim || (listening ? 'Listening…' : 'Tap the mic and say it.')}
              </p>
              <button
                type="button"
                className={`cap__mic${listening ? ' cap__mic--on' : ''}`}
                aria-label={listening ? 'Stop listening' : 'Start listening'}
                aria-pressed={listening}
                onClick={listening ? stopListening : startListening}
              >
                <Icon name={listening ? 'stop' : 'mic'} size={34} filled />
              </button>
              <p className="cap__hint">{listening ? 'Tap to finish' : 'Like “call mom Friday 3pm”'}</p>
              {note && <p className="cap__note">{note}</p>}
              <div className="cap__modes">
                <button type="button" className="cap__switch" onClick={() => switchTo('type')}>
                  <Icon name="keyboard" size={18} />
                  Type
                </button>
                <button type="button" className="cap__switch" onClick={() => switchTo('chat')}>
                  <Icon name="chat" size={18} />
                  Chat
                </button>
              </div>
            </div>
          ) : (
            <form className="cap__type" onSubmit={onSubmit} aria-label="Capture">
              <textarea
                ref={field}
                className="cap__field"
                aria-label="What's on your mind?"
                placeholder="Call mom Friday 3pm #personal"
                rows={3}
                enterKeyHint="done"
                autoComplete="off"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    submit(text);
                  }
                }}
              />
              {note && <p className="cap__note">{note}</p>}
              <div className="cap__bar">
                {canSpeak && (
                  <button type="button" className="cap__switch" onClick={() => switchTo('voice')}>
                    <Icon name="mic" size={18} />
                    Speak
                  </button>
                )}
                <button type="button" className="cap__switch" onClick={() => switchTo('chat')}>
                  <Icon name="chat" size={18} />
                  Chat
                </button>
                <button type="submit" className="ui-btn ui-btn--primary ui-btn--md cap__add" disabled={!text.trim()}>
                  Add
                </button>
              </div>
              <p className="cap__tip">Dates, times and #sphere are understood. You can fix the job, project and date after.</p>
            </form>
          )}
        </>
      )}
    </Sheet>
  );
}

/** One filed capture: kind, title, job, project and date, all editable in place. */
function ReceiptCard({
  job,
  filed: f,
  spheres,
  projects,
  onUndo,
  onKind,
  onEdit,
}: {
  job: Job;
  filed: Filed;
  spheres: Sphere[];
  projects: Project[];
  onUndo: () => void;
  onKind: (k: Kind) => void;
  onEdit: (change: Partial<Filed>, fields: { title?: boolean; filing?: boolean; when?: boolean }) => void;
}): JSX.Element {
  const [title, setTitle] = useState(f.title);
  useEffect(() => setTitle(f.title), [f.title]);
  const choices = projects.filter((p) => p.sphereKey === f.sphereKey || p.id === f.projectId);
  const sphereChoices = spheres.some((s) => s.key === f.sphereKey)
    ? spheres
    : [...spheres, { key: f.sphereKey, name: f.sphereKey } as Sphere];

  function commitTitle(): void {
    const t = title.trim();
    if (!t) setTitle(f.title);
    else if (t !== f.title) onEdit({ title: t }, { title: true });
  }

  function setWhen(value: string): void {
    if (f.kind === 'task') {
      onEdit({ when: value || null }, { when: true });
      return;
    }
    if (!value || !f.when) return;
    const startsAt = f.allDay ? isoAt(value, '12:00') : new Date(value).toISOString();
    const shift = Date.parse(startsAt) - Date.parse(f.when);
    const endsAt = f.endsAt ? new Date(Date.parse(f.endsAt) + shift).toISOString() : null;
    onEdit({ when: startsAt, endsAt }, { when: true });
  }

  return (
    <div className="cap__receipt-card cap__rc">
      <div className="cap__rc-top">
        <span className="cap__receipt-icon" aria-hidden="true">
          <Icon name="check" size={20} />
        </span>
        <p className="cap__receipt-text">{job.summary}</p>
      </div>
      <div className="cap__kinds" role="radiogroup" aria-label="Kind">
        {KINDS.map((k) => (
          <button
            key={k.value}
            type="button"
            role="radio"
            aria-checked={f.kind === k.value}
            className={`cap__kind${f.kind === k.value ? ' cap__kind--on' : ''}`}
            onClick={() => onKind(k.value)}
          >
            {k.label}
          </button>
        ))}
      </div>
      <label className="cap__rc-field cap__rc-field--wide">
        <span>Title</span>
        <input
          className="settings-input"
          aria-label="Title"
          value={title}
          maxLength={300}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={commitTitle}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitTitle();
            }
          }}
        />
      </label>
      <label className="cap__rc-field">
        <span>Job</span>
        <select
          className="settings-input"
          aria-label="Job"
          value={f.sphereKey}
          onChange={(e) => {
            const key = e.target.value;
            const keep = projects.find((p) => p.id === f.projectId)?.sphereKey === key;
            onEdit({ sphereKey: key, projectId: keep ? f.projectId : null }, { filing: true });
          }}
        >
          {sphereChoices.map((s) => (
            <option key={s.key} value={s.key}>
              {s.name}
            </option>
          ))}
        </select>
      </label>
      <label className="cap__rc-field">
        <span>Project</span>
        <select
          className="settings-input"
          aria-label="Project"
          value={f.projectId ?? ''}
          onChange={(e) => {
            const pid = e.target.value || null;
            const p = projects.find((x) => x.id === pid);
            onEdit({ projectId: pid, sphereKey: p?.sphereKey ?? f.sphereKey }, { filing: true });
          }}
        >
          <option value="">None</option>
          {choices.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </label>
      {f.kind === 'task' && (
        <label className="cap__rc-field">
          <span>Due</span>
          <input className="settings-input" aria-label="Due" type="date" value={f.when ?? ''} onChange={(e) => setWhen(e.target.value)} />
        </label>
      )}
      {f.kind === 'event' && f.when && (
        <label className="cap__rc-field">
          <span>Starts</span>
          <input
            className="settings-input"
            aria-label="Starts"
            type={f.allDay ? 'date' : 'datetime-local'}
            value={f.allDay ? localDay(f.when) : localInput(f.when)}
            onChange={(e) => setWhen(e.target.value)}
          />
        </label>
      )}
      {job.note && <p className="cap__note">{job.note}</p>}
      <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm cap__rc-undo" onClick={onUndo}>
        <Icon name="undo" size={16} />
        Undo
      </button>
    </div>
  );
}
